import { useState, useCallback, useRef, useEffect } from 'react';
import { Search } from '@carbon/icons-react';
import { debounce } from '@/utils/debounce';
import { Input } from '@/components/ui';

import './search-input-style.scss';

interface SearchInputProps {
  value?: string;
  onChange: (searchTerm: string) => void;
  placeholder?: string;
  debounceMs?: number;
  disabled?: boolean;
  isActive?: boolean; // Highlight when search has text
  id?: string;
}

const DEFAULT_DEBOUNCE_MS = 500;

const SearchInput = ({
  value = '',
  onChange,
  placeholder = 'Search',
  debounceMs = DEFAULT_DEBOUNCE_MS,
  disabled = false,
  isActive = false,
  id = 'search-input',
}: SearchInputProps) => {
  const [localValue, setLocalValue] = useState(value);

  // Create debounced onChange callback
  const debouncedOnChange = useRef(
    debounce((searchTerm: string) => {
      onChange(searchTerm);
    }, debounceMs),
  ).current;

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedOnChange.cancel();
    };
  }, [debouncedOnChange]);

  // Sync with external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
      debouncedOnChange(newValue);
    },
    [debouncedOnChange],
  );

  return (
    <span className="search-input-wrapper">
      <Input
        id={id}
        className={`search-input ${isActive ? 'search-input-active' : ''}`}
        placeholder={placeholder}
        value={localValue}
        onChange={handleChange}
        disabled={disabled}
        type="text"
      />
      <Search className="small-icon search-input-icon" />
    </span>
  );
};

export { SearchInput };
